import { useLanguage } from '../LanguageContext';
import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert, // ✅ Import Alert
} from "@mui/material";
import BanModal from "./BanModal";
import axiosInstance from "../axiosInstance";

const ManualBans = () => {
  const { t } = useLanguage();
  const [playerIdentity, setPlayerIdentity] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);

  // ✅ Open Ban Modal with entered details (Bans apply to all servers)
  const handleBanClick = async () => {
    if (!playerIdentity.trim()) {
      setSnackbar({ open: true, message: t("❌ Player Identity is required."), severity: "error" });
      return;
    }
    let playerName = t("Unknown");
    const identity = playerIdentity.trim();
    try {
      const { data } = await axiosInstance.get(`/api/get_player_by_identity/${encodeURIComponent(identity)}`);
      playerName = data.player_name || t("Unknown");
    } catch (error) {
      console.error('Could not load player name:', error);
    }
    setSelectedPlayerData({ player_identity: identity, player_name: playerName });
    setShowBanModal(true);
  };

  const handleBanSaved = (data) => {
    setSnackbar({ open: true, message: data.message || t("Player banned successfully."), severity: 'success' });
  };

  // ✅ Function to Kick a Player (Only applies to the correct server)
  const handleKickPlayer = async () => {
    if (!playerIdentity.trim()) {
      setSnackbar({ open: true, message: t("❌ Player Identity is required."), severity: "error" });
      return;
    }

    try {
      const response = await axiosInstance.post(
        "/api/kick_player",
        { player_uid: playerIdentity }, // ✅ Backend determines the correct server
        { withCredentials: true }
      );
    
      let message, severity;
      if (response.data.success) {
          message = response.data.message; // Use server message
          severity = "success";
      } else {
          message = response.data.error || t("❌ Kick failed.");
          severity = "error";
      }

      setSnackbar({ open: true, message, severity });
    } catch (err) {
      console.error("❌ Error kicking player:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t("❌ Failed to kick player."),
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" sx={{ color: "#d4af37", mb: 2 }}>{t("Manual Ban & Kick")}</Typography>

      <Box display="flex" flexDirection="column" gap={2}>

        {/* ✅ Updated Search Bar for Player Identity (UID) */}
        <TextField
          label={t("Exact player ID (UID)")}
          helperText={t("Paste the full ID. To find a player by name, use the Player List.")}
          variant="outlined"
          fullWidth
          value={playerIdentity}
          onChange={(e) => setPlayerIdentity(e.target.value)}
        />

        {/* ✅ Action Buttons */}
        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="contained"
            color="error"
            onClick={handleBanClick}
            sx={{ minWidth: 120 }}
          >{t("Ban Player")}</Button>

          <Button
            variant="contained"
            onClick={handleKickPlayer}
            sx={{
              minWidth: 120,
              backgroundColor: "#d4af37",
              color: "white",
              "&:hover": { backgroundColor: "#b8860b" },
            }}
          >{t("Kick Player")}</Button>
        </Box>

        {/* ✅ Ban Modal */}
        {selectedPlayerData && (
          <BanModal
            open={showBanModal}
            onClose={() => setShowBanModal(false)}
            playerData={selectedPlayerData}
            onSaved={handleBanSaved}
          />
        )}

        {/* ✅ Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {t(snackbar.message)}
        </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ManualBans;
